import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import Header from "../../Components/Header/Header";
import CustomPagination from "../../Components/CustomPagination/CustomPagination";
import EmptyData from "../../Components/EmptyData/EmptyData";

import {
  apiCallGetWalletFunds,
  apiCallGetArtrunerWallet,
} from "../../Core/Apicall";

import {
  setLoadingFalse,
  setLoadingTrue,
} from "../../Config/Actions/loadingActions";

import moment from "moment";

import "./WalletFunds.css";

import Icons from "../../Constants/Icons";

// ─── Chip config ─────────────────────────────────────────────────────────────
const WALLET_CHIPS = [
  { label: "USER WALLET", route: "/walletFunds", api: "user" },
  { label: "ARTRUNNER WALLET", route: "/ArtrunnerWallet", api: "artrunner" },
];

// ─── ChipBar (same style as ArtLover) ────────────────────────────────────────
function ChipBar({ selected, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px",
        background: "rgba(6, 101, 228, 0.15)",
        border: "1px solid rgba(6, 101, 228, 0.35)",
        borderRadius: "999px",
        width: "fit-content",
        boxShadow: "0 4px 24px rgba(6, 101, 228, 0.15)",
        marginBottom: "24px",
      }}
    >
      {WALLET_CHIPS.map((c) => {
        const active = selected === c.label;
        return (
          <button
            key={c.label}
            onClick={() => onChange(c)}
            style={{
              height: "35px",
              padding: "0 28px",
              borderRadius: "999px",
              border: "none",
              background: active ? "rgba(6, 101, 228, 0.75)" : "transparent",
              color: active ? "#FFFFFF" : "rgba(255,255,255,0.55)",
              boxShadow: active ? "0 2px 12px rgba(6, 101, 228, 0.4)" : "none",
              fontSize: "13px",
              fontWeight: "600",
              letterSpacing: "1px",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "all .25s ease",
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Transaction options ──────────────────────────────────────────────────────
const transactionOptions = [
  { label: "All Transactions", value: "" },
  { label: "Wallet Topup", value: "WalletTopup" },
  { label: "Refund", value: "Refund" },
  { label: "Event Booking", value: "EventBooking" },
  { label: "Ticket Cancel Refund", value: "TicketCancelRefund" },
  { label: "Event Cancel Refund", value: "EventCancelRefund" },
];

// ─── Badge class helper ───────────────────────────────────────────────────────
const badgeClass = (tf) =>
  tf === "WalletTopup" ? "wallet-topup"
    : tf === "Refund" ? "refund"
      : tf === "EventBooking" ? "event-booking"
        : tf === "TicketCancelRefund" ? "ticket-cancel-refund"
          : tf === "EventCancelRefund" ? "event-cancel-refund"
            : "wallet-default";

const badgeLabel = {
  WalletTopup: "Wallet Topup",
  Refund: "Refund",
  EventBooking: "Event Booking",
  TicketCancelRefund: "Ticket Cancel Refund",
  EventCancelRefund: "Event Cancel Refund",
};

// ─── View Details Modal ──────────────────────────────────────────────────────
function ViewDetailsModal({ isOpen, onClose, bookingData }) {
  if (!isOpen || !bookingData) return null;

  const { bookingDetails, eventData } = bookingData;
  const bookingInfo = bookingDetails?.bookingData || {};
  const bookedUsers = bookingDetails?.bookedUserDetails || [];
  const eventBookedUser = bookingDetails?.eventBookedUser || {};

  // Extract event info from eventData
  const eventName = eventData?.eventDetail?.eventName || "N/A";
  const eventStartDate = eventData?.eventDetail?.eventStartDate
    ? moment(eventData.eventDetail.eventStartDate).format("DD MMM YYYY")
    : "N/A";
  const eventStartTime = eventData?.eventDetail?.eventStartTime || "N/A";
  const eventEndTime = eventData?.eventDetail?.eventEndTime || "N/A";

  // Get pricing info
  const ticketPrice = bookingInfo.ticketPrice || 0;
  const quantity = bookingInfo.quantity || 0;
  const totalAmount = bookingInfo.totalAmount || 0;
  const bookedSeatsType = bookingInfo.bookedSeatsType || "N/A";
  const bookingStatus = bookingInfo.bookingStatus || "N/A";
  const paymentStatus = bookingInfo.paymentStatus || "N/A";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content-popup" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Booking Details</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Event Info */}
          <div className="modal-section">
            <div className="modal-event-header">
              <h3 className="modal-event-name">{eventName}</h3>
              <span className={`modal-booking-status ${bookingStatus === "Cancelled" ? "status-cancelled" : "status-booked"}`}>
                {bookingStatus}
              </span>
            </div>
            <div className="modal-event-datetime">
              <span>📅 {eventStartDate}</span>
              <span>🕐 {eventStartTime} - {eventEndTime}</span>
            </div>
          </div>

          {/* Booking Summary */}
          <div className="modal-section">
            <h4>Booking Summary</h4>
            <div className="modal-summary-grid">
              <div className="modal-summary-item">
                <span className="modal-summary-label">Booking Type</span>
                <span className="modal-summary-value">{bookingInfo.bookingType || "N/A"}</span>
              </div>
              <div className="modal-summary-item">
                <span className="modal-summary-label">Seats Type</span>
                <span className="modal-summary-value">{bookedSeatsType}</span>
              </div>
              <div className="modal-summary-item">
                <span className="modal-summary-label">Quantity</span>
                <span className="modal-summary-value">{quantity}</span>
              </div>
              <div className="modal-summary-item">
                <span className="modal-summary-label">Ticket Price</span>
                <span className="modal-summary-value">${ticketPrice}</span>
              </div>
              <div className="modal-summary-item">
                <span className="modal-summary-label">Total Amount</span>
                <span className="modal-summary-value">${totalAmount}</span>
              </div>
              <div className="modal-summary-item">
                <span className="modal-summary-label">Payment Status</span>
                <span className={`modal-payment-status ${paymentStatus === "Success" ? "status-success" : "status-refunded"}`}>
                  {paymentStatus}
                </span>
              </div>
            </div>
          </div>

          {/* Booked Users */}
          <div className="modal-section">
            <h4>Booked Users ({bookedUsers.length})</h4>
            <div className="modal-users-list">
              {bookedUsers.map((user, index) => (
                <div key={index} className="modal-user-item">
                  <img
                    src={user.profileImageURL || Icons.ic_user}
                    alt={user.name || "User"}
                    className="modal-user-image"
                  />
                  <div className="modal-user-details">
                    <div className="modal-user-name">{user.name || "N/A"}</div>
                    <div className="modal-user-email">{user.email || "N/A"}</div>
                    <div className="modal-user-type">{user.userType || "User"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Booked By */}
          <div className="modal-section">
            <h4>Booked By</h4>
            <div className="modal-user-item">
              <img
                src={eventBookedUser.profileImage || Icons.ic_user}
                alt={eventBookedUser.name || "User"}
                className="modal-user-image"
              />
              <div className="modal-user-details">
                <div className="modal-user-name">{eventBookedUser.name || "N/A"}</div>
                <div className="modal-user-email">{eventBookedUser.email || "N/A"}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-close-btn-bottom" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function WalletFunds() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useSelector((state) => state.isLoading);

  // derive active chip from URL
  const [selectedChip, setSelectedChip] = useState(
    () => WALLET_CHIPS.find((c) => c.route === location.pathname) || WALLET_CHIPS[0]
  );

  const [search, setSearch] = useState("");
  const [transactionFor, setTransactionFor] = useState("");
  const [walletData, setWalletData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [initialPage, setInitialPage] = useState(0);

  // Store grouped data for ArtRunner
  const [groupedWalletData, setGroupedWalletData] = useState([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // sync chip when browser back/forward
  useEffect(() => {
    const matched = WALLET_CHIPS.find((c) => c.route === location.pathname);
    if (matched && matched.label !== selectedChip.label) {
      setSelectedChip(matched);
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  // ── chip switch ──
  const handleChipChange = (chip) => {
    setSelectedChip(chip);
    setSearch("");
    setTransactionFor("");
    setInitialPage(0);
    setGroupedWalletData([]);
    navigate(chip.route);
  };

  // ── fetch ──
  const fetchWalletFunds = async (selectedPage = 1) => {
    try {
      dispatch(setLoadingTrue());
      const body = {
        offset: selectedPage,
        limit: 10,
        search: search?.trim(),
        transactionFor,
      };

      const response =
        selectedChip.api === "artrunner"
          ? await apiCallGetArtrunerWallet(body)
          : await apiCallGetWalletFunds(body);

      if (selectedChip.api === "artrunner") {
        // Store grouped data for ArtRunner
        const groupedData = response?.data?.walletHistory || [];
        setGroupedWalletData(groupedData);

        // Flatten transactions for display
        const flattened = [];
        groupedData.forEach((eventGroup) => {
          (eventGroup.transactions || []).forEach((transaction) => {
            flattened.push({
              ...transaction,
              _eventGroup: {
                eventName: eventGroup.eventName,
                eventDate: eventGroup.eventDate,
                eventTime: eventGroup.eventTime,
                eventPrice: eventGroup.eventPrice,
              }
            });
          });
        });
        setWalletData(flattened);
      } else {
        setWalletData(response?.data?.walletHistory || []);
        setGroupedWalletData([]);
      }

      setPagination({
        totalRecords: response?.data?.totalRecords || 0,
        pages: Math.ceil(
          (response?.data?.totalRecords || 0) / (response?.data?.limit || 10)
        ),
      });
      setInitialPage(selectedPage);
    } catch (error) {
      console.error("Wallet Funds Error:", error);
    } finally {
      dispatch(setLoadingFalse());
    }
  };

  useEffect(() => {
    const t = setTimeout(() => fetchWalletFunds(1), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, transactionFor, selectedChip]);

  // ── user name/image key differs between user wallet & artrunner wallet ──
  const getUserData = (item) =>
    selectedChip.api === "artrunner" ? item?.artRunnerData : item?.userData;

  const getAmount = (item) =>
    selectedChip.api === "artrunner" ? item?.finalAmount : item?.amount;

  // ── handle view details ──
  const handleViewDetails = (item) => {
    setSelectedBooking({
      bookingDetails: item.bookingDetails,
      eventData: item.eventData,
    });
    setIsModalOpen(true);
  };

  // ── close modal ──
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBooking(null);
  };

  return (
    <div>
      <Header name={"Wallet Funds"} />

      <ChipBar selected={selectedChip.label} onChange={handleChipChange} />
      <div className="Event-custom-style">
        <div className="lastWeekEndDiv Event-AlignSelf-stretch">

          <div className="user_title">
            {selectedChip.label === "ARTRUNNER WALLET"
              ? "Artrunner Wallet Transactions"
              : "Wallet Transactions"}
          </div>

          {/* ── FILTERS ── */}
          <div
            style={{
              display: "flex",
              gap: "15px",
              marginBottom: "20px",
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {/* SEARCH */}
            <div className="custom-box-search-div">
              <img className="custom-image" alt="" src={Icons.ic_search} />
              <input
                type="text"
                className="searchTextInput"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* DROPDOWN */}
            <div style={{ position: "relative", width: "250px" }}>
              <select
                value={transactionFor}
                onChange={(e) => setTransactionFor(e.target.value)}
                style={{
                  width: "100%",
                  height: "44px",
                  background: "#001B44",
                  border: "1px solid #FF2E93",
                  borderRadius: "10px",
                  padding: "0px 42px 0px 16px",
                  color: "#FFFFFF",
                  fontSize: "14px",
                  fontWeight: "600",
                  outline: "none",
                  cursor: "pointer",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  boxSizing: "border-box",
                }}
              >
                {transactionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div
                style={{
                  position: "absolute",
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: "#FFFFFF",
                  fontSize: "12px",
                }}
              >
                ▼
              </div>
            </div>
          </div>

          {/* ── TABLE HEADER ── */}
          <div
            className={`wallet-table-header ${selectedChip.api === "artrunner" ? "artrunner-header" : ""
              }`}
          >
            <div className="wallet-col-user">User</div>
            <div className="wallet-col-transaction-for">Transaction For</div>
            <div className="wallet-col-transaction-type">Transaction Type</div>
            <div className="wallet-col-amount">Amount</div>
            <div className="wallet-col-balance">Wallet Balance</div>
            <div className="wallet-col-date">Transaction Date</div>

            {selectedChip.api === "artrunner" && (
              <div className="wallet-col-view">View</div>
            )}
          </div>

          {/* ── TABLE BODY ── */}
          {selectedChip.api === "artrunner" ? (

            groupedWalletData.map((eventGroup, groupIndex) => {
              const { eventName, eventDate, eventTime, transactions = [] } = eventGroup;

              return (
                <React.Fragment key={groupIndex}>
                  {/* Event Group Header */}
                  <div className="event-group-header">
                    <span className="event-group-name">
                      {eventName || "Unknown Event"}
                    </span>
                    <span className="event-group-date">
                      {eventDate ? moment(eventDate).format('DD/MM/YYYY') : '-'}
                    </span>
                    <span className="event-group-date">
                      {eventTime ? moment(eventTime, 'HH:mm:ss').format('hh:mm A') : '-'}
                    </span>
                  </div>

                  {/* Transactions for this event */}
                  {
                    transactions.map((item, index) => {
                      const userData = item?.bookingDetails?.eventBookedUser;
                      return (
                        <div
                          key={item._id || index}
                          className={`wallet-table-row ${selectedChip.api === "artrunner" ? "artrunner-row" : ""
                            }`}
                        >
                          {/* USER */}
                          <div className="wallet-col-user">
                            <div className="wallet-user-wrapper">
                              <img
                                src={userData?.profileImage || Icons.ic_user}
                                alt="profile"
                                className="wallet-user-image"
                              />
                              <div className="wallet-user-info">
                                <div className="wallet-user-name">
                                  {userData?.name || "-"}
                                </div>
                                <div className="wallet-user-email">
                                  {userData?.email || "-"}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* TRANSACTION FOR - FIXED: Use transactionFor from item */}
                          <div className="wallet-col-transaction-for">
                            <span className={`wallet-transaction-badge ${badgeClass(item?.transactionFor)}`}>
                              {badgeLabel[item?.transactionFor] || item?.transactionFor || "-"}
                            </span>
                          </div>

                          {/* TRANSACTION TYPE */}
                          <div className="wallet-col-transaction-type">
                            <div
                              className={
                                item?.transactionType === "Credit"
                                  ? "wallet-status-active"
                                  : "wallet-status-inactive"
                              }
                            >
                              {item?.transactionType}
                            </div>
                          </div>

                          {/* AMOUNT */}
                          <div className="wallet-col-amount wallet-amount-text">
                            ${item?.finalAmount || 0}
                          </div>

                          {/* BALANCE */}
                          <div className="wallet-col-balance wallet-amount-text">
                            ${item?.availableBalance || 0}
                          </div>

                          {/* DATE */}
                          <div className="wallet-col-date wallet-common-text">
                            {moment(item?.createdAt).format("DD/MM/YYYY hh:mm A")}
                          </div>

                          {/* VIEW */}
                          <div className="wallet-col-view">
                            <button
                              className="view-details-btn"
                              onClick={() => handleViewDetails(item)}
                              title="View Details"
                            >
                              <img
                                src={Icons.ic_green_eye}
                                alt="View"
                                className="view-icon"
                              />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  }
                </React.Fragment>
              );
            })
          ) : (
            // User Wallet: Flat display
            walletData?.map((item, index) => {
              const userData = getUserData(item);
              return (
                <div
                  key={index}
                  className="wallet-table-row"
                >
                  {/* USER */}
                  <div className="wallet-col-user">
                    <div className="wallet-user-wrapper">
                      <img
                        src={userData?.profileImage || Icons.ic_user}
                        alt="profile"
                        className="wallet-user-image"
                      />
                      <div className="wallet-user-info">
                        <div className="wallet-user-name">
                          {userData?.name || "-"}
                        </div>
                        <div className="wallet-user-email">
                          {userData?.email || "-"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* TRANSACTION FOR - FIXED: Use transactionFor from item */}
                  <div className="wallet-col-transaction-for">
                    <span className={`wallet-transaction-badge ${badgeClass(item?.transactionFor)}`}>
                      {badgeLabel[item?.transactionFor] || item?.transactionFor || "-"}
                    </span>
                  </div>

                  {/* TRANSACTION TYPE */}
                  <div className="wallet-col-transaction-type">
                    <div
                      className={
                        item?.transactionType === "Credit"
                          ? "wallet-status-active"
                          : "wallet-status-inactive"
                      }
                    >
                      {item?.transactionType}
                    </div>
                  </div>

                  {/* AMOUNT */}
                  <div className="wallet-col-amount wallet-amount-text">
                    ${getAmount(item) || 0}
                  </div>

                  {/* BALANCE */}
                  <div className="wallet-col-balance wallet-amount-text">
                    ${item?.availableBalance || 0}
                  </div>

                  {/* DATE */}
                  <div className="wallet-col-date wallet-common-text">
                    {moment(item?.createdAt).format("DD/MM/YYYY hh:mm A")}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* EMPTY */}
      {
        walletData?.length === 0 && !isLoading && (
          <div className="empty-centered">
            <EmptyData />
          </div>
        )
      }

      {/* PAGINATION */}
      {
        walletData?.length !== 0 && (
          <div className="Custom-Pagination">
            <CustomPagination
              pageCount={pagination?.pages || 1}
              forcePage={initialPage - 1}
              onPageChange={({ selected }) => {
                const page = selected + 1;
                setInitialPage(page);
                fetchWalletFunds(page);
              }}
            />
          </div>
        )
      }

      {/* ── VIEW DETAILS MODAL ── */}
      <ViewDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        bookingData={selectedBooking}
      />
    </div >
  );
}

export default WalletFunds;